import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import QRCodeStyling from "qr-code-styling"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Divider, Icon, Input, Modal } from "semantic-ui-react"

import actions from "actions"
import api from "api"
import AppIcon from "src/images/appIcon.png"
import { useAppDispatch, useAppSelector } from "store"

export const SuggestedMFAApps = () => {
  const { t } = useTranslation()

  return (
    <p>
      {t("entry.mfa.suggestedApps")}
      Google Authenticator (
      <a
        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
        target="_blank"
        rel="noopener noreferrer"
      >
        iOS
      </a>{" "}
      /{" "}
      <a
        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
        target="_blank"
        rel="noopener noreferrer"
      >
        Android
      </a>
      ) {t("generic.and")} Authy (
      <a href="https://apps.apple.com/us/app/twilio-authy/id494168017" target="_blank" rel="noopener noreferrer">
        iOS
      </a>{" "}
      /{" "}
      <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer">
        Android
      </a>
      )
    </p>
  )
}

const EnforceMFA = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [uri, setUri] = useState(null)
  const [otpVerifier, setOtpVerifier] = useState("")
  const otpQrRef = useRef(null)

  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)

  const openMfaModal = useCallback(() => {
    setModalOpen(true)
    api.auth.generateOtpUri(
      (check) => {
        setUri(check.uri)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [])

  const checkMfaCode = () => {
    const loadingId = toast.loading(t("entry.mfa.statusMessageLoading"), { id: "enableMFA" })
    setLoading(true)
    api.auth.enableOtp(
      { otp: otpVerifier },
      () => {
        setLoading(false)
        setModalOpen(false)
        setOtpVerifier("")
        toast.success(t("entry.mfa.statusMessageSuccess"), { id: loadingId })
        dispatch(actions.user.enableMfa())
      },
      (err) => {
        setLoading(false)
        toast.error(err.message, { id: loadingId })
        setOtpVerifier("")
      },
    )
  }

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: uri,
        image: AppIcon,
        dotsOptions: {
          color: "#14435B",
        },
        backgroundOptions: {
          color: "#ffffff",
        },
        imageOptions: {
          margin: 4,
        },
      }),
    [uri],
  )

  useEffect(() => {
    if (modalOpen && !qrCode._container) {
      qrCode.append(otpQrRef.current)
    }
  }, [modalOpen, qrCode])

  return (
    <>
      <h3>
        <Icon name="lock" style={{ paddingRight: "10px" }} />
        {t("entry.mfa.title")} {t("generic.required")}
      </h3>
      <p>
        {t("entry.mfa.requiredByOrg", {
          orgName: user.ownerOrganisation?.name,
        })}
      </p>
      <p>{t("entry.mfa.explainer")}</p>
      <SuggestedMFAApps />
      <Divider />

      <div style={{ textAlign: "right" }}>
        <Button
          primary
          style={{ maxWidth: "fit-content" }}
          icon={"lock"}
          content={t("entry.mfa.enableAction")}
          onClick={openMfaModal}
        />
      </div>

      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={modalOpen}>
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          {t("entry.mfa.enableAction")}
        </Modal.Header>
        <Modal.Content scrolling>
          <h3>{t("generic.step")} 1</h3>
          <p>{t("entry.mfa.step1")}</p>
          <SuggestedMFAApps />

          <h3>{t("generic.step")} 2</h3>
          <p>{t("entry.mfa.step2")}</p>
          <div style={{ width: "70%", margin: "10px auto", textAlign: "center" }} ref={otpQrRef} />

          <h3>{t("generic.step")} 3</h3>
          <p>{t("entry.mfa.step3")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e) => {
                e.keyCode === 13 && checkMfaCode()
              }}
              size="large"
              onChange={(e) => setOtpVerifier(e.target.value)}
              value={otpVerifier}
              placeholder="123456"
              autoFocus
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setModalOpen(false)
            }}
          />
          <Button
            primary
            content={t("generic.verify")}
            loading={loading}
            onClick={() => checkMfaCode()}
            disabled={otpVerifier.replaceAll(" ", "").length !== 6}
          />
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default EnforceMFA
